import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { retryWithDelay } from './service.model';
import { getBundledTranslation } from '../model/localization.model';

export type TranslationDoc = { [key: string]: string | TranslationDoc };
export type TranslationConfig = { locale: string[] };

/**
 * TranslationDocCollection stores two types of translations:
 * Bundled and Custom Translation. To Access call [Bundled, CustomTranslation]
 * **/
export type TranslationDocCollection = [TranslationDoc, TranslationDoc | undefined];


@Injectable({
  providedIn: 'root',
})
export class GetTranslationService {
  constructor(private http: HttpClient) {}

  public getTranslationStrings(language: string, tenantKey: string, loadCustom: boolean): Observable<TranslationDocCollection> {
    return forkJoin([

      this.getDefaultTranslationCheckBundle(language).pipe(
        catchError(() => {
          console.warn(`Cidaas: Failed to load default ${language}.json. Falling back to default en.json`);
          return this.fallbackTranslations('en');
        })
      ),
      !loadCustom && of(undefined) || this.getTenantTranslation(language, tenantKey)
    ]);
  }

  /**
   * Return default translation, if the language is bundled with cidaas. Else return empty
   * @param {string} language locale formatted in RFC standards. example [en, en-US]
   * @returns {Observable<TranslationDoc>} translations related to the locale
   * **/
  getDefaultTranslationCheckBundle(language: string):Observable<TranslationDoc> {
    const bundledLocale = getBundledTranslation(language);
    // given language is not part of cidaas bundle, don't return
    if (!bundledLocale) {
      return of({})
    }
    return this.getDefaultTranslation(bundledLocale);
  }

  getDefaultTranslation(language: string): Observable<TranslationDoc> {
    return this.http.get<TranslationDoc>(environment.cdn_url + '/' + environment.build_path + 'assets/i18n/' + language + '.json').pipe(
      catchError(() => {
        console.warn('Fallback to local translation assets');
        return this.fallbackTranslations(language);
      })
    );
  }

  getTenantTranslation(language: string, tenantKey: string): Observable<TranslationDoc | undefined> {
    return this.http.get<TranslationDoc>(environment.cdn_url + '/custom-translation/' + tenantKey + '/' + language + '.json').pipe(
      catchError(() => {
        console.warn('Tenant specific translation missing');
        return of(undefined);
      })
    );
  }

  getTenantTranslationConfig(tenantKey: string): Observable<TranslationConfig> {
    return this.http.get<TranslationConfig>(environment.cdn_url + '/custom-translation/' + tenantKey + '/config.json').pipe(
      retryWithDelay(),
      catchError(() => {
        console.warn('Cidaas: Tenant specific Config is missing!');
        return of({locale: []});
      })
    );
  }

  public fallbackTranslations(language: string): Observable<TranslationDoc> {
    const contextPath = ''; // for custom deployment & mapping, e.g. /identity
    if (window.location.hostname === 'localhost') {
      return this.http.get<TranslationDoc>('./assets/i18n/' + language + '.json');
    } else {
      return this.http.get<TranslationDoc>('.' + contextPath + '/assets/i18n/' + language + '.json')
        .pipe(retryWithDelay());
    }
  }
}
